/**
 * API client for backend communication.
 * Uses Axios with JWT token authentication.
 */
import axios, { AxiosInstance, AxiosError } from 'axios';

/**
 * IMPORTANT:
 * - In the browser we want same-origin calls (no CORS): baseURL = '/api'
 * - On the server (SSR) Axios needs an absolute URL. We route via the local web server,
 *   which will reverse-proxy /api/* to the backend container.
 */
const publicBase = process.env.NEXT_PUBLIC_API_URL || '/api';
const serverBase =
  process.env.INTERNAL_API_BASE_URL || `http://10.0.0.9:${process.env.PORT || '3889'}/api`;

const API_BASE_URL = typeof window === 'undefined' ? serverBase : publicBase;

// Create Axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor: Add JWT token from localStorage
apiClient.interceptors.request.use(
  (config) => {
    // Only access localStorage on client-side (browser)
    if (typeof window !== 'undefined') {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);


// Response interceptor: Handle 401 errors (unauthorized)
apiClient.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (typeof window !== 'undefined') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (username: string) => {
    const response = await apiClient.post('/auth/login', { username });
    return response.data;
  },
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
  getUsers: async () => {
    const response = await apiClient.get('/auth/users');
    return response.data;
  },
};

// Business Types API
export const businessTypesAPI = {
  list: async (category?: string) => {
    const params = category ? { category } : {};
    const response = await apiClient.get('/business-types', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await apiClient.get(`/business-types/${id}`);
    return response.data;
  },
  getPaths: async (id: number) => {
    const response = await apiClient.get(`/business-types/${id}/paths`);
    return response.data;
  },
  create: async (data: any) => {
    const response = await apiClient.post('/business-types', data);
    return response.data;
  },
  createGuidancePath: async (data: any) => {
    const response = await apiClient.post('/business-types/guidance-paths', data);
    return response.data;
  },
  listPaths: async (businessTypeId: number) => {
    const response = await apiClient.get(`/business-types/${businessTypeId}/paths`);
    return response.data;
  },
  createPath: async (data: any) => {
    const response = await apiClient.post('/business-types/guidance-paths', data);
    return response.data;
  },
};

// Providers API
export const providersAPI = {
  list: async (filters?: {
    business_type_id?: number;
    min_rating?: number;
    max_price?: number;
    status?: string;
    user_latitude?: number;
    user_longitude?: number;
  }) => {
    const response = await apiClient.get('/providers', { params: filters });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await apiClient.get(`/providers/${id}`);
    return response.data;
  },
  updatePricing: async (id: number, pricing: Record<string, number>) => {
    const response = await apiClient.put(`/providers/${id}/pricing`, { pricing });
    return response.data;
  },
  updateStatus: async (id: number, status: string, reason?: string) => {
    const response = await apiClient.put(`/providers/${id}/status`, { status, reason });
    return response.data;
  },
};

// orders API
export const ordersAPI = {
  create: async (data: any) => {
    const response = await apiClient.post('/orders', data);
    return response.data;
  },
  list: async (filters?: { status?: string; business_type_id?: number }) => {
    const response = await apiClient.get('/orders', { params: filters });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await apiClient.get(`/orders/${id}`);
    return response.data;
  },
  updateStatus: async (id: number, status: string, notes?: string) => {
    const response = await apiClient.put(`/orders/${id}/status`, { status, notes });
    return response.data;
  },
  accept: async (id: number, notes?: string) => {
    // Always send a body, even if notes is undefined
    const body = notes ? { notes } : { notes: null };
    const response = await apiClient.post(`/orders/${id}/accept`, body);
    return response.data;
  },
  reject: async (id: number) => {
    const response = await apiClient.post(`/orders/${id}/reject`);
    return response.data;
  },
  markPaymentComplete: async (id: number) => {
    const response = await apiClient.post(`/orders/${id}/payment`);
    return response.data;
  },
};

// Notifications API
export const notificationsAPI = {
  list: async () => {
    const response = await apiClient.get('/notifications');
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await apiClient.get('/notifications/unread-count');
    return response.data;
  },
  markAsRead: async (notificationId: number) => {
    const response = await apiClient.put(`/notifications/${notificationId}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await apiClient.put('/notifications/read-all');
    return response.data;
  },
};

// Ratings API
export const ratingsAPI = {
  create: async (orderId: number, stars: number, feedback?: string) => {
    const response = await apiClient.post(`/orders/${orderId}/rating`, { stars, feedback });
    return response.data;
  },
};

// Complaints API
export const complaintsAPI = {
  create: async (orderId: number, description: string) => {
    const response = await apiClient.post(`/complaints/orders/${orderId}/complaint`, { description });
    return response.data;
  },
  list: async (status?: string) => {
    const params = status ? { status } : {};
    const response = await apiClient.get('/complaints', { params });
    return response.data;
  },
  getById: async (id: number) => {
    const response = await apiClient.get(`/complaints/${id}`);
    return response.data;
  },
  update: async (id: number, data: { status?: string; resolution?: string }) => {
    const response = await apiClient.put(`/complaints/${id}`, data);
    return response.data;
  },
};

// Admin API
// Chat API
export const chatAPI = {
  sendMessage: async (message: string, conversationHistory?: Array<{ role: string; content: string }>) => {
    const response = await apiClient.post('/chat/message', {
      message,
      conversation_history: conversationHistory,
    });
    return response.data;
  },
  listKnowledge: async () => {
    const response = await apiClient.get('/chat/knowledge');
    return response.data;
  },
  uploadKnowledge: async (file: File, title: string, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    if (description) {
      formData.append('description', description);
    }
    const response = await apiClient.post('/chat/knowledge', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  deleteKnowledge: async (id: number) => {
    const response = await apiClient.delete(`/chat/knowledge/${id}`);
    return response.data;
  },
};

export const adminAPI = {
  getStats: async () => {
    const response = await apiClient.get('/admin/stats');
    return response.data;
  },
  exportorders: async (filters?: { start_date?: string; end_date?: string; status?: string }) => {
    const response = await apiClient.get('/admin/export/orders', {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  },
  exportComplaints: async () => {
    const response = await apiClient.get('/admin/export/complaints', {
      responseType: 'blob',
    });
    return response.data;
  },
};

export default apiClient;

